/** THE MEDIA PROBE — where the owner's voice actually stops.
 *
 * Build 14 read `start idle` and the box read WEBRTC_NEGOTIATION_STALLED, and
 * between those two facts sat five and a half unaccounted seconds. The audit log
 * proved the mic was not dead — the 10:56 call forwarded its first frame 8.8s
 * after ontrack, 0.8s past the watchdog — but it could not say WHY the frame was
 * late, because the box only sees frames that arrive. This measures the half the
 * box cannot: what the device did between opening the microphone and the first
 * RTP packet leaving it.
 *
 * ── WHAT IT SEPARATES ───────────────────────────────────────────────────────
 * Three failures look identical from the box (ontrack, then silence):
 *
 *   1. CAPTURE SUPPRESSED — the track exists but produces no samples. `lvl`
 *      stays 0.000 and `samples` stays flat. This is the shape the manual-audio
 *      hypothesis predicts, and the reason that hypothesis is testable at all.
 *   2. TRANSPORT LATE — samples are flowing, RTP is not. `lvl` moves while
 *      `rtp` is still `—`. The pair has not been nominated yet.
 *   3. PATH DEAD — RTP leaves, the box never counts a frame. `rtp` lands early
 *      and the box still stalls.
 *
 * A number in each column says which one happened. Nobody has to infer it from
 * a call that felt slow.
 *
 * ── WHY IT LIVES IN ITS OWN MODULE ──────────────────────────────────────────
 * It could have been another variant on VoiceDaemonEvent. It is not, deliberately:
 * that union is a CONTRACT consumed by every window, and widening a contract to
 * carry a diagnostic makes the diagnostic permanent. This is a probe. It attaches
 * to a peer, reports, and can be removed by deleting one file and three call
 * sites — which is the property a temporary instrument should have.
 *
 * ── COST ────────────────────────────────────────────────────────────────────
 * One getStats() every 250ms, from peer creation until the first RTP packet is
 * seen, capped. Then it stops polling and holds the reading. No timer survives
 * the call.
 */

/** The remote candidate count is read from the ANSWER SDP the box sent us — so the
 *  candidate-filter change in voice_media_edge/media.py is provable FROM THE PHONE,
 *  by a number that moves, rather than inferred from a call that felt faster. */
export interface MediaProbeReading {
  /** WHICH ENGINE PRODUCED THIS — the engine's own word for itself, never a
   *  guess made from the shell it happens to be running in.
   *
   *  It used to be one field called `surface`, set by
   *  `probeBegin(isNativeShell() ? 'native' : 'browser')` INSIDE THE BROWSER
   *  ENGINE. So the browser engine running in the native shell rendered
   *  `MEDIA·ios`, and the line could not distinguish the two engines at all — on
   *  2026-08-07 that made a reading consistent with either one, which is the
   *  state a flip must never be measured from. Each engine now states its own
   *  identity and neither branches. */
  engine: 'native' | 'browser';
  /** WHICH DEVICE produced it — the phone-vs-desktop question the audit log could
   *  not answer, and still worth answering separately: `browser engine on the
   *  phone` is a real and important state, and one field cannot hold both facts.
   *  Supplied by the selection site (voiceDaemon), the only place that knows both
   *  and therefore the only place they cannot disagree. `null` until it has been
   *  told — never inferred here. */
  shell: 'ios' | 'web' | null;
  startedAt: number;
  /** ms from negotiation start. null = has not happened (never a reassuring 0). */
  micMs: number | null;
  offerMs: number | null;
  answerMs: number | null;
  channelMs: number | null;
  /** The moment the FIRST RTP packet left the device. The number this exists for. */
  firstRtpMs: number | null;
  /** DISTINCT candidates the box offered — deduped across m-lines.
   *
   *  This used to be a raw count of `a=candidate:` LINES, and the line count is
   *  not the candidate count: the answer carries two m-lines (audio and the
   *  control channel) and every candidate appears on BOTH. So the filter's
   *  `kept: 4` and the readout's `(4c)` were four LINES — two addresses, the
   *  tailnet one and the LAN one, exactly as media.py:71 rules. Reading it as
   *  four candidates is what made `pairs 2/13` look like arithmetic that could
   *  not close. */
  remoteCandidates: number | null;
  /** The raw line count, kept beside the deduped one — it is the number the
   *  box's own VOICE_B_ICE_FILTERED `kept` reports, so the two records can be
   *  laid side by side without one of them silently meaning something else. */
  remoteCandidateLines: number | null;
  /** What the ICE agent actually held when the pairs were counted. A pair count
   *  with no factors is a number nobody can check: 13 says nothing until it can
   *  be read against the local and remote sets it was formed from. */
  localCandidateCount: number | null;
  remoteCandidateCount: number | null;
  /** INSTANTANEOUS level at the last sample. Kept, but it is NOT the capture
   *  verdict: sampled every 250ms in a quiet room a working microphone reads
   *  0.000 too, and reading a conclusion off this field was the first version's
   *  defect. */
  audioLevel: number | null;
  /** PEAK level across the whole probe window — the field that actually answers
   *  "did the microphone ever produce anything". One breath moves it; suppressed
   *  capture cannot. */
  peakLevel: number | null;
  /** MONOTONIC seconds of audio the capture has produced, and the strongest
   *  single discriminator there is: it grows whenever samples exist, whether the
   *  room is loud, quiet, or silent. Flat at 0 while the track is live is
   *  suppression and nothing else. */
  totalSamples: number | null;
  /** Cumulative energy. Same argument as samples, different stat, because WebKit
   *  does not implement every field and two chances beat one. */
  energy: number | null;
  /** What the native side says about the audio contract at the moment the probe
   *  closed — `manual=y en=n` — so the flags and the silence are read together
   *  rather than correlated from memory across two screens. */
  nativeAudio: string;
  /** Live track state at the last sample — a muted or ended track is its own answer. */
  trackState: string;
  /** The pair that won, once one has. */
  pair: string;
  /** How many pairs the ICE agent had to work through, and how many succeeded. */
  pairsTotal: number | null;
  pairsSucceeded: number | null;
  packetsSent: number | null;
  /** Did an outbound-rtp audio report EVER appear in getStats? WebKit does not
   *  implement every stat, and on 2026-08-05 the probe reported `RTP NONE` for two
   *  readings where the box independently counted first frames at 8.8s and 8.9s.
   *  "No packets were sent" and "this browser never showed me the counter" are
   *  different findings, and the line must not render them the same way. */
  outboundStatSeen: boolean;
  /** Bytes on the selected pair — a second, independent witness that SOMETHING is
   *  leaving, for exactly the case where the per-stream counter is unreadable. */
  pairBytesSent: number | null;
  /** IS THE STATS LOOP ACTUALLY RUNNING? `probeAttach` needs a JS
   *  RTCPeerConnection, and the native engine does not have one — its peer lives
   *  in Swift. So on the native path nothing polls, and every field this module
   *  derives from getStats stays null.
   *
   *  Without this flag those nulls rendered exactly like measurements: `peak —`,
   *  `samples —`, and worst of all `RTP NONE` on a call the box was receiving
   *  audio from throughout. That is the THIRD readout on this surface to state a
   *  conclusion from a source that was never wired — after the CallKit line's
   *  orphaned gate and `MEDIA·ios` naming the shell instead of the engine. An
   *  unattached probe now says so, and concludes nothing. */
  attached: boolean;
  /** Set when the probe stops without ever seeing RTP. Only ever set on an
   *  ATTACHED probe: giving up is a finding, and an instrument that never ran has
   *  not earned one. */
  gaveUp: boolean;
  /** ── THE ROUTE, AND THE VOLUME IT PLAYS AT (build 21, Ghost finding 2) ──
   *
   *  `audioRouteChanged` has carried `outputVolume` since build 20 and PhoneHost
   *  stored only `speaker`, so the three-way discriminator that build shipped
   *  ("speaker true + outputVolume 0.0 => the system volume is the cause") could
   *  not actually be read anywhere. A discriminator that cannot discriminate
   *  costs a cycle, so both facts land here as fields rather than in the
   *  free-text native line — which is rewritten at 10Hz by the playout witness
   *  and would clobber them.
   *
   *  null = NOBODY MEASURED, never "not on speaker" and never "volume zero". */
  routeSpeaker: boolean | null;
  outputVolume: number | null;
  /** The last route the device REFUSED to move to, with its reason. A refused
   *  route was previously emitted by Swift into an event union that did not list
   *  it and a listener that did not exist, so it was quieter than the comments
   *  claimed (Ghost finding 3). */
  routeFailed: string;
  /** The last duck/resume command that failed to reach the engine. */
  gainFailed: string;
  /** How many ducks the engine REFUSED for naming a turn that was not playing.
   *  This is the signal that Part B's turn scoping is working — without it a
   *  refusal and a duck that never fired look identical from the outside. */
  duckRefusals: number;
  /** A mute the box was never told about. The native side has emitted this since
   *  build 19 and nothing subscribed, so a mute that did not land was silent. */
  muteUndelivered: boolean;
  /** ── THE ASSERTION, ON THE READOUT (Fix 2, owner-ruled 2026-08-10) ────────
   *
   *  The one fact build 23 moved and the one fact nothing could see. On
   *  2026-08-10 she stopped a fraction of a second into every turn with every
   *  other field green, and the box could not say whether the `playing` verb was
   *  computed false, sent and refused, or never sent — because the device
   *  announced none of the three.
   *
   *  `''` means nothing has been asserted on this peer yet, which on a
   *  `playing`-verb binary is itself the finding. */
  playbackAssert: string;
  /** The last assertion that did NOT reach the box, with the facts that decide
   *  which half failed. Printed loudly: an undelivered assertion is the
   *  difference between her being transcribed and not. */
  playbackUndelivered: string;
  /** WHEN THE PEER DIED UNDER THE PROBE, if it did. Without this, a connection
   *  torn down at 10s and a connection that simply never sent anything both read
   *  `RTP NONE` — and on 2026-08-05 that cost a wrong conclusion: the desktop
   *  control's socket dropped at +8s and its peer closed at +10s, so `RTP NONE`
   *  and `pairs 0/3` were read off a closed connection and taken as ICE failure.
   *  An absent number must say whether the instrument was measuring or watching
   *  a corpse. */
  closedMs: number | null;
  closeReason: string;
  /** Peer state at the last sample — `closed` here is the whole story. */
  endState: string;
}

type Listener = (r: MediaProbeReading) => void;

const SAMPLE_MS = 250;
/** Long enough to outlive the box's 8s watchdog and still record the late frame
 *  that watchdog fires ahead of — the 10:56 call would have been caught by this. */
const MAX_PROBE_MS = 25_000;
/** Kept for the console handle only. Small: this is evidence for one sitting. */
const HISTORY = 8;

const listeners = new Set<Listener>();
const history: MediaProbeReading[] = [];

let current: MediaProbeReading | null = null;
let timer: number | null = null;
let peer: RTCPeerConnection | null = null;
/** Set once per document by probeSetShell(). Null means nobody has said, which
 *  the line renders as `—` rather than assuming a device. */
let currentShell: 'ios' | 'web' | null = null;

function blank(engine: 'native' | 'browser'): MediaProbeReading {
  return {
    engine,
    // The shell is a standing fact about this document, not a per-call one, so it
    // is carried across readings rather than re-supplied at every negotiation.
    shell: currentShell,
    startedAt: Date.now(),
    micMs: null, offerMs: null, answerMs: null, channelMs: null, firstRtpMs: null,
    remoteCandidates: null, remoteCandidateLines: null,
    localCandidateCount: null, remoteCandidateCount: null,
    audioLevel: null, peakLevel: null, totalSamples: null,
    energy: null, nativeAudio: '',
    trackState: '', pair: '', pairsTotal: null, pairsSucceeded: null,
    packetsSent: null, outboundStatSeen: false, pairBytesSent: null,
    attached: false, gaveUp: false,
    routeSpeaker: null, outputVolume: null, routeFailed: '', gainFailed: '',
    duckRefusals: 0, muteUndelivered: false,
    playbackAssert: '', playbackUndelivered: '',
    closedMs: null, closeReason: '', endState: '',
  };
}

/** THE MEASURED ROUTE, from the surface that receives `audioRouteChanged`.
 *
 *  Both halves travel together because they answer one question between them:
 *  a call on the speaker at zero volume and a call on the receiver at full
 *  volume are both inaudible, and the readout has to be able to say which. */
export function probeNoteRoute(speaker: boolean, outputVolume: number | null): void {
  if (!current) return;
  current.routeSpeaker = speaker;
  if (outputVolume != null && Number.isFinite(outputVolume)) {
    current.outputVolume = outputVolume;
  }
  publish();
}

/** A ROUTE THAT REFUSED TO MOVE (Ghost finding 3). */
export function probeNoteRouteFailed(detail: string): void {
  if (!current) return;
  current.routeFailed = detail;
  publish();
}

/** A DUCK THE ENGINE REFUSED — Part B rejecting a stale turn (build 22). */
export function probeNoteDuckRefused(): void {
  if (!current) return;
  current.duckRefusals += 1;
  publish();
}

/** A MUTE THE BOX WAS NEVER TOLD ABOUT (build 22). */
export function probeNoteMuteUndelivered(): void {
  if (!current) return;
  current.muteUndelivered = true;
  publish();
}

/** ── NO FIELD RENDERS WITHOUT SAYING WHEN IT WAS TAKEN (owner ruling
 *  2026-08-10) ────────────────────────────────────────────────────────────
 *
 *  This line cost a round. `assert UNDELIVERED (... peer=false open=false)` was
 *  a snapshot from 09:47:57.960 and it rendered beside a CURRENT
 *  `assert peer:playing`, with nothing to say they were 1.2 seconds apart. The
 *  peer had reached connected at 09:47:59.144 and the channel had opened at
 *  09:47:59.717 — so a blast radius was written against a peer failure that did
 *  not exist, while the real defect (a missing import, three files away) went
 *  unread for another round.
 *
 *  A monotonic sequence AND an offset from this reading's own origin: the
 *  sequence orders two fields written in the same millisecond, the offset says
 *  how stale one of them is. Both, because either alone can be misread. */
let seq = 0;
function stamp(): string {
  seq += 1;
  const at = current ? ((Date.now() - current.startedAt) / 1000).toFixed(1) : '?';
  return `#${seq}@${at}s`;
}

/** WHAT THE DEVICE ASSERTED ABOUT PLAYBACK, AND WHETHER IT LEFT (Fix 2).
 *
 *  `owner` is which side is speaking for the track — `peer` on a `playing`-verb
 *  binary, `window` on every older one. Two calls, because "what was derived"
 *  and "what arrived at the box" are different facts and folding them together
 *  is what made 2026-08-10 unreadable from either end. */
export function probeNotePlaybackAssert(playing: boolean, owner: string): void {
  if (!current) return;
  current.playbackAssert = `${owner}:${playing ? 'playing' : 'not-playing'}`
    + ` ${stamp()}`;
  publish();
}

/** AN ASSERTION THE BOX WAS NEVER TOLD. Never silent, no exceptions. */
export function probeNotePlaybackUndelivered(detail: string): void {
  if (!current) return;
  current.playbackUndelivered = `${stamp()} ${detail.slice(0, 120)}`;
  publish();
}

/** A GAIN COMMAND THAT DID NOT LAND (Ghost, build 21).
 *
 *  duck() and resume() both swallowed their rejection. A failed resume is how a
 *  call goes silent with every other field green, which is the exact state two
 *  builds were shipped in — so it gets a field rather than a dropped promise. */
export function probeNoteGainFailed(detail: string): void {
  if (!current) return;
  current.gainFailed = detail.slice(0, 80);
  publish();
}

/** THE ENGINE'S OWN STATS, on a path where nothing polls (Ghost finding 4).
 *
 *  `probeAttach` needs a JS RTCPeerConnection and the native engine's peer lives
 *  in Swift, so every getStats-derived field stays null on this path and renders
 *  `unpolled`. RexyMediaEngine has been emitting all of these at 10Hz; build 20
 *  wired one of the four and claimed the rest. This is the other three. */
export function probeNoteNativeStats(
  v: { totalSamples?: number; pairsTotal?: number; pairsSucceeded?: number },
): void {
  if (!current) return;
  // -1 is the engine's "not measured" and must not print as a measured zero.
  if (v.totalSamples != null && Number.isFinite(v.totalSamples) && v.totalSamples >= 0) {
    current.totalSamples = v.totalSamples;
  }
  if (v.pairsTotal != null && Number.isFinite(v.pairsTotal)) {
    current.pairsTotal = v.pairsTotal;
    current.pairsSucceeded = Number.isFinite(v.pairsSucceeded ?? NaN)
      ? (v.pairsSucceeded as number) : 0;
  }
  publish();
}

/** The native audio contract, handed in by the surface that can read it. The probe
 *  does not reach for CallKit itself — it reports what it is told, next to what it
 *  measured, which is the only place the two facts can be compared at a glance. */
export function probeNoteNative(line: string, forStartedAt?: number): void {
  if (!current) return;
  // A NOTE BELONGS TO THE READING IT WAS TAKEN FOR. The caller reads native
  // diagnostics asynchronously; if a renegotiation opened a new reading while
  // that promise was in flight, the answer describes the OLD one. Writing it into
  // the new reading is how a tail ends up contradicting the numbers beside it.
  if (forStartedAt !== undefined && forStartedAt !== current.startedAt) return;
  current.nativeAudio = line;
  publish();
}

function publish(): void {
  if (!current) return;
  const snapshot = { ...current };
  listeners.forEach((fn) => { try { fn(snapshot); } catch { /* a listener never stalls a probe */ } });
}

/** ONE LINE, READABLE ON A PHONE. Unmeasured fields render as `—`, never as 0 —
 *  the difference between "no packets yet" and "measured zero packets" is the
 *  whole diagnosis and the readout must not blur it. */
export function formatProbe(r: MediaProbeReading | null): string {
  if (!r) return 'MEDIA · idle';
  const s = (ms: number | null) => (ms == null ? '—' : `${(ms / 1000).toFixed(1)}s`);
  // PEAK, NOT INSTANTANEOUS. The first version rendered the live level and invited
  // a verdict off a field that reads 0.000 in a quiet room whether or not capture
  // is working. Peak and samples are the fields that can carry that weight.
  const peak = r.peakLevel == null ? '—' : r.peakLevel.toFixed(3);
  // A DASH ON AN UNATTACHED PROBE MEANS "NOBODY LOOKED", NOT "NOTHING THERE",
  // and the two must not print the same character. `samples` is the only
  // getStats-derived field with no native road across yet, so it is the only one
  // that still needs saying out loud.
  const samples = r.totalSamples != null
    ? `${r.totalSamples.toFixed(2)}s`
    : r.attached ? '—' : 'unpolled';
  const parts = [
    `mic ${s(r.micMs)}`,
    `peak ${peak}`,
    `samples ${samples}`,
    `offer ${s(r.offerMs)}`,
    // CANDIDATES, NOT LINES — and the line count beside it when they differ, so
    // this reading and the box's VOICE_B_ICE_FILTERED `kept` can be compared
    // without either one quietly meaning the other.
    `ans ${s(r.answerMs)}${
      // `!= null` throughout, not `!== null`: a reading assembled anywhere but
      // blank() can leave a field UNDEFINED, and a strict null check renders the
      // literal string "undefined" into the one line that exists to be precise.
      r.remoteCandidates == null
        ? ''
        : ` (${r.remoteCandidates}c${
            r.remoteCandidateLines != null && r.remoteCandidateLines !== r.remoteCandidates
              ? `/${r.remoteCandidateLines}L`
              : ''
          })`
    }`,
    `dc ${s(r.channelMs)}`,
    // A TORN-DOWN PEER IS NOT A SILENT ONE. If the connection died before RTP,
    // the line says so with the moment and the reason, because "none arrived" and
    // "nothing was left to arrive on" are different findings.
    `RTP ${
      r.firstRtpMs != null
        ? s(r.firstRtpMs)
        // NOTHING IS POLLING. Every branch below reasons about counters this
        // module never read, so none of them may run: an unattached probe has
        // measured nothing about RTP and must say that instead of concluding it.
        : !r.attached
          ? 'unpolled (engine owns the peer)'
        // A TEARDOWN OUTRANKS AN ABSENT COUNTER. If the peer died, that is WHY
        // there was nothing to read, and it is the more complete statement.
        : r.closedMs != null
          ? `NONE (peer ${r.closeReason} ${s(r.closedMs)})`
          // UNREADABLE IS NOT NONE. The probe ran its full window and this browser
          // never showed the counter, so it has measured nothing about whether
          // packets left. Saying NONE there is a claim it cannot make.
          : !r.outboundStatSeen && r.gaveUp
            ? `unreadable (no outbound-rtp${r.pairBytesSent ? `, pair tx ${Math.round(r.pairBytesSent / 1024)}kB` : ''})`
            : r.gaveUp ? 'NONE' : '—'
    }`,
  ];
  if (r.energy != null) parts.push(`nrg ${r.energy.toFixed(4)}`);
  if (r.pairsTotal != null) {
    // The factors ride with the total. `pairs 2/13 · 6L×2R` is checkable at a
    // glance; `pairs 2/13` never was.
    const factors = r.localCandidateCount != null && r.remoteCandidateCount != null
      ? ` ${r.localCandidateCount}L×${r.remoteCandidateCount}R`
      : '';
    parts.push(`pairs ${r.pairsSucceeded ?? 0}/${r.pairsTotal}${factors}`);
  }
  if (r.pair) parts.push(r.pair);
  if (r.trackState && r.trackState !== 'live') parts.push(`track ${r.trackState}`);
  // ── THE THIRD SILENCE CAUSE, MADE READABLE (build 21, Ghost finding 2) ────
  // Printed only once something measured it: a route line that appears on every
  // reading whether or not a route was read would be the same false-light class
  // this readout keeps removing. `vol` rides with it because the pair is the
  // discriminator — speaker-at-zero and receiver-at-full are both inaudible.
  if (r.routeSpeaker != null) {
    parts.push(`out ${r.routeSpeaker ? 'speaker' : 'receiver'}${
      r.outputVolume != null ? ` vol ${r.outputVolume.toFixed(2)}` : ''
    }`);
  }
  if (r.routeFailed) parts.push(`route FAILED (${r.routeFailed})`);
  if (r.gainFailed) parts.push(`gain FAILED (${r.gainFailed})`);
  // A REFUSAL IS A HEALTHY SIGNAL, not an error: it is Part B rejecting a duck
  // for a turn that is no longer playing. Printed only when it happened.
  if (r.duckRefusals > 0) parts.push(`duck refused x${r.duckRefusals}`);
  if (r.muteUndelivered) parts.push('mute UNDELIVERED');
  // THE ASSERTION, ALWAYS RENDERED — including when it is absent, because on a
  // peer-owned binary an absent assertion is the whole diagnosis and a blank
  // field would read as "not measured" rather than "nobody has spoken".
  parts.push(`assert ${r.playbackAssert || 'NONE'}`);
  if (r.playbackUndelivered) parts.push(`assert UNDELIVERED (${r.playbackUndelivered})`);
  if (r.nativeAudio) parts.push(r.nativeAudio);
  // THE TWO FACTS, SIDE BY SIDE AND NEVER FOLDED TOGETHER. `MEDIA·ios · eng
  // browser` is the sentence this line could not say before, and it is exactly
  // the state the phone is in before the flip. An unset shell reads `—` rather
  // than defaulting to `web`: nobody told this reading which device it came from,
  // and guessing is how the field became untrustworthy the first time.
  return `MEDIA·${r.shell ?? '—'} · eng ${r.engine} · ${parts.join(' · ')}`;
}

/** THE CAPTURE VERDICT, STATED RATHER THAN INFERRED. Three fields decide it and
 *  the rule is written once, here, so the readout and any later analysis cannot
 *  disagree about what the numbers meant. */
export function captureVerdict(r: MediaProbeReading | null): 'unknown' | 'producing' | 'silent' {
  if (!r) return 'unknown';
  if ((r.totalSamples ?? 0) > 0.05) return 'producing';
  if ((r.peakLevel ?? 0) > 0.0005) return 'producing';
  if ((r.energy ?? 0) > 0) return 'producing';
  // NOT PROVEN SILENT UNTIL THE INSTRUMENT ACTUALLY WATCHED. If WebKit reported
  // none of the three fields there is nothing to conclude from, and saying
  // "silent" there would be the same mistake in the other direction.
  const measured = r.totalSamples !== null || r.peakLevel !== null || r.energy !== null;
  if (!measured) return 'unknown';
  return r.micMs === null ? 'unknown' : 'silent';
}

export function subscribeMediaProbe(fn: Listener): () => void {
  listeners.add(fn);
  if (current) fn({ ...current });
  return () => { listeners.delete(fn); };
}

export function currentProbe(): MediaProbeReading | null {
  return current ? { ...current } : null;
}

/** Negotiation has begun. Everything downstream is measured from here — the same
 *  t0 the box's own timeline starts from, so the two readings can be laid side by
 *  side without translating between clocks. */
export function probeBegin(engine: 'native' | 'browser'): void {
  stopTimer();
  current = blank(engine);
  publish();
}

/** WHICH DEVICE THIS DOCUMENT IS RUNNING ON, told to the probe rather than worked
 *  out by it.
 *
 *  This module has no imports and this design keeps it that way: reaching for
 *  `isNativeShell()` here would put Capacitor behind every consumer of the probe,
 *  including the desktop engine. The one gated selection site
 *  (voiceDaemon.ts) already knows both the shell and the engine it chose, so it
 *  states both — and because they are set from the same place, they can never
 *  drift into disagreeing about what ran where.
 *
 *  Applies to the CURRENT reading as well as future ones: the shell does not
 *  change mid-document, so a reading already in flight was always on this device
 *  and should say so. */
export function probeSetShell(shell: 'ios' | 'web'): void {
  currentShell = shell;
  if (current && current.shell !== shell) {
    current.shell = shell;
    publish();
  }
}

export function probeMark(
  field: 'micMs' | 'offerMs' | 'answerMs' | 'channelMs',
  extra?: {
    remoteCandidates?: number; remoteCandidateLines?: number; trackState?: string;
  },
): void {
  if (!current) return;
  current[field] = Date.now() - current.startedAt;
  if (extra?.remoteCandidates !== undefined) current.remoteCandidates = extra.remoteCandidates;
  if (extra?.remoteCandidateLines !== undefined) {
    current.remoteCandidateLines = extra.remoteCandidateLines;
  }
  if (extra?.trackState !== undefined) current.trackState = extra.trackState;
  publish();
}

/** Count what the box actually offered us. This is the client-side measure of the
 *  candidate set — the number the media.py filter is meant to move. */
export function countCandidates(
  sdp: string | undefined,
): { unique: number; lines: number } | null {
  if (!sdp) return null;
  const lines = sdp.match(/^a=candidate:.*$/gm) ?? [];
  // a=candidate:<foundation> <component> <transport> <priority> <addr> <port> typ <type>
  // The SAME candidate is repeated on every m-line, so the identity is the
  // transport/address/port/type tuple — never the line, and never the
  // foundation alone, which aiortc reuses across components.
  const seen = new Set<string>();
  for (const line of lines) {
    const p = line.slice('a=candidate:'.length).trim().split(/\s+/);
    if (p.length < 8) continue;
    seen.add(`${p[2]}/${p[4]}:${p[5]}/${p[7]}`.toLowerCase());
  }
  return { unique: seen.size, lines: lines.length };
}

export function probeAttach(pc: RTCPeerConnection): void {
  if (!current) return;
  current.attached = true;
  peer = pc;
  stopTimer();
  timer = window.setInterval(() => { void sample(); }, SAMPLE_MS);
  void sample();
}

/** The peer is going away. The REASON travels with it: a probe that stopped
 *  because the connection was torn down has measured nothing about ICE, and must
 *  not be read as though it had. */
export function probeEnd(reason = 'closed'): void {
  stopTimer();
  if (current) {
    if (current.closedMs === null) {
      current.closedMs = Date.now() - current.startedAt;
      current.closeReason = reason;
    }
    // GIVING UP IS A CONCLUSION, AND ONLY AN ATTACHED PROBE HAS EARNED ONE. An
    // unattached probe measured nothing about RTP, so it cannot conclude none
    // arrived — see the `attached` field for the third time that mistake was
    // made on this surface.
    if (current.firstRtpMs === null && current.attached) current.gaveUp = true;
    publish();
  }
  peer = null;
}

/** FIRST RTP, REPORTED BY AN ENGINE THAT OWNS ITS OWN PEER.
 *
 *  The native engine reads `outbound-rtp.packetsSent` in Swift and has always
 *  known this number — it emitted it as a transport event, which the readout does
 *  not read, so the line said `RTP —` through a call the box was receiving audio
 *  from. The measurement existed; only the wire to the readout was missing. */
export function probeNoteFirstRtp(ms: number): void {
  if (!current || current.firstRtpMs !== null || !Number.isFinite(ms) || ms < 0) return;
  current.firstRtpMs = ms;
  // An engine that can say when RTP left has, by saying so, witnessed the
  // outbound counter — which is exactly what this flag records.
  current.outboundStatSeen = true;
  publish();
}

/** CAPTURE LEVEL from an engine that polls its own stats. Same field the browser
 *  path reads from `media-source.audioLevel`, arriving by a different road: peak
 *  is what answers "did the microphone ever produce anything", and it was reading
 *  `—` on native purely because nothing carried the value across. */
export function probeNoteLevel(level: number): void {
  if (!current || !Number.isFinite(level)) return;
  current.audioLevel = level;
  if (current.peakLevel === null || level > current.peakLevel) current.peakLevel = level;
  publish();
}

function stopTimer(): void {
  if (timer !== null) { window.clearInterval(timer); timer = null; }
}

async function sample(): Promise<void> {
  const pc = peer;
  const r = current;
  if (!pc || !r) return;
  const age = Date.now() - r.startedAt;

  // A CLOSED PEER IS NOT A MEASUREMENT. Stats read after teardown come back
  // stale or empty, and on 2026-08-05 exactly that produced `pairs 0/3` off a
  // connection that had been closed for seconds — which was then read as ICE
  // failing. The probe stops at the teardown and says when it happened.
  const state = `${pc.connectionState ?? ''}`;
  if (state) r.endState = state;
  if (state === 'closed' || state === 'failed') {
    if (r.closedMs === null) {
      r.closedMs = age;
      r.closeReason = state === 'failed' ? 'failed' : 'died';
    }
    if (r.firstRtpMs === null) r.gaveUp = true;
    stopTimer();
    publish();
    return;
  }

  let stats: RTCStatsReport;
  try {
    stats = await pc.getStats();
  } catch {
    // A probe never takes a call down. A getStats that throws is a probe that
    // stops, not a negotiation that fails.
    stopTimer();
    return;
  }
  if (current !== r) return; // a newer negotiation started under us

  let packets: number | null = null;
  let pairsTotal = 0;
  let pairsSucceeded = 0;
  // THE FACTORS, so the pair count can be CHECKED. ICE forms a pair for every
  // compatible (local, remote) combination, so a bare "13" is unfalsifiable —
  // it could be a healthy full checklist or a set doubled by an m-line that was
  // never bundled, and the reading could not tell those apart.
  let localCands = 0;
  let remoteCands = 0;
  let winner: any = null;
  const byId = new Map<string, any>();

  stats.forEach((report: any) => { byId.set(report.id, report); });
  stats.forEach((report: any) => {
    // LOOSER THAN `kind === 'audio'` ON PURPOSE. WebKit has been observed to omit
    // the discriminator, and this negotiation carries exactly one media stream —
    // an audio track and a control data channel — so an outbound-rtp report here
    // cannot be anything else. The strict match is what made two readings say
    // `RTP NONE` while the box was counting frames.
    if (report.type === 'outbound-rtp'
        && report.kind !== 'video' && report.mediaType !== 'video') {
      r.outboundStatSeen = true;
      if (typeof report.packetsSent === 'number') packets = report.packetsSent;
    } else if (report.type === 'media-source' && (report.kind === 'audio' || report.mediaType === 'audio')) {
      // THE CAPTURE ANSWER. Non-zero energy means the microphone is producing
      // samples regardless of what the transport is doing — which is exactly the
      // fact that separates a suppressed capture from a late pair.
      if (typeof report.audioLevel === 'number') {
        r.audioLevel = report.audioLevel;
        // PEAK IS HELD. A microphone that produced one breath at any point in the
        // window has proved itself, and a 250ms sampler that only kept the latest
        // value would throw that proof away between samples.
        r.peakLevel = Math.max(r.peakLevel ?? 0, report.audioLevel);
      }
      if (typeof report.totalSamplesDuration === 'number') r.totalSamples = report.totalSamplesDuration;
      if (typeof report.totalAudioEnergy === 'number') r.energy = report.totalAudioEnergy;
    } else if (report.type === 'candidate-pair') {
      pairsTotal += 1;
      if (report.state === 'succeeded') pairsSucceeded += 1;
      // `nominated` is the spec field; WebKit has historically reported `selected`.
      // Accept either rather than read blank on the one browser this is built for.
      if (report.state === 'succeeded' && (report.nominated || report.selected)) winner = report;
    } else if (report.type === 'local-candidate') {
      localCands += 1;
    } else if (report.type === 'remote-candidate') {
      remoteCands += 1;
    } else if (report.type === 'track' && typeof report.ended === 'boolean') {
      if (!r.trackState) r.trackState = report.ended ? 'ended' : 'live';
    }
  });

  r.pairsTotal = pairsTotal;
  r.pairsSucceeded = pairsSucceeded;
  r.localCandidateCount = localCands;
  r.remoteCandidateCount = remoteCands;
  r.packetsSent = packets;

  if (winner && typeof winner.bytesSent === 'number') r.pairBytesSent = winner.bytesSent;

  if (winner && !r.pair) {
    const local = byId.get(winner.localCandidateId);
    const remote = byId.get(winner.remoteCandidateId);
    if (remote) {
      const addr = remote.address ?? remote.ip ?? '?';
      r.pair = `${local?.candidateType ?? '?'}→${addr}:${remote.port ?? '?'} ${remote.protocol ?? ''}`.trim();
    }
  }

  // THE MOMENT THE FIRST PACKET LEFT. Recorded once — a later sample must not
  // move it, or the number stops meaning "first".
  if (r.firstRtpMs === null && packets !== null && packets > 0) {
    r.firstRtpMs = age;
    publish();
    // The pair is worth one more read now that media is flowing, then the probe
    // is done: no timer outlives the question it was opened to answer.
    stopTimer();
    return;
  }

  if (age > MAX_PROBE_MS) {
    r.gaveUp = true;
    stopTimer();
  }
  publish();
}

// A console handle for a desktop reading, where there is no phone band to render
// into. The line is the same line; only the place it is read differs.
try {
  (window as any).__rexyMediaProbe = {
    current: () => currentProbe(),
    line: () => formatProbe(currentProbe()),
    history: () => history.slice(),
  };
} catch { /* no window: a test environment, and the probe is inert there */ }

subscribeMediaProbe((r) => {
  if (r.firstRtpMs !== null || r.gaveUp) {
    history.push(r);
    while (history.length > HISTORY) history.shift();
    // eslint-disable-next-line no-console
    console.info('[mediaProbe]', formatProbe(r));
  }
});
